import { AssetStatus } from '@ynomia/core/dist/blueprint';
import pick from 'lodash/pick';
import {
  Asset, AssetUpdate, Layer, PlannedTwinID,
} from '../config/types';
import { getLayerPathFromLayerID } from './layers';
import { displayDateInProjectTime } from './time';

/**
 * This converts an asset to a flat human readable record
 */
export const assetToHumanReadableRecord = (
  asset: Asset | undefined,
  layersKeyedById: Map<string, Layer>,
): Record<string, string> => {
  if (!asset) return {};
  const { destination, fields, label } = asset;
  const currentAssetLayerID = destination?.layerID || '';
  const currentAssetTwinID = destination?.twinID?.[0] || '';
  const layerPath = getLayerPathFromLayerID(layersKeyedById, currentAssetLayerID);
  const flatFields: Record<string, string> = Object.keys(fields ?? {}).reduce((acc, fieldKey) => ({
    ...acc,
    [`fields.${fieldKey}`]: fields?.[fieldKey],
  }), {});

  return {
    label,
    twinID: currentAssetTwinID,
    layer: `${layerPath} (${currentAssetLayerID})`,
    ...flatFields,
  };
};

/**
 * This converts the asset update tool response to a flat human readable record
 */
export const assetUpdateToHumanReadableRecord = (
  asset: AssetUpdate | undefined,
  layersKeyedById: Map<string, Layer>,
): Record<string, string> => {
  if (!asset) return {};
  const {
    label, twinID, fields, layerID,
  } = asset;
  const layerPath = getLayerPathFromLayerID(layersKeyedById, layerID);
  const flatFields: Record<string, string> = Object.keys(fields ?? {}).reduce((acc, fieldKey) => ({
    ...acc,
    [`fields.${fieldKey}`]: fields?.[fieldKey],
  }), {});

  return {
    label,
    twinID,
    layer: `${layerPath} (${layerID})`,
    ...flatFields,
  };
};

export const getReadableRecordDiff = (
  currentRecord: Record<string, string>,
  newRecord: Record<string, string>,
) => Object.keys({ ...currentRecord, ...newRecord })
  .map((key) => {
    const currentValue = currentRecord[key] || '';
    const newValue = newRecord[key] || '';

    const getIsValueChanged = (): 'new' | 'updated' | 'none' => {
      if (currentValue !== newValue) {
        return !currentValue ? 'new' : 'updated';
      }
      return 'none';
    };

    return {
      key,
      currentValue,
      isValueChanged: getIsValueChanged(),
      newValue,
    };
  });

export const getAssetChanges = (
  currentAsset: Asset | undefined,
  updatedAsset: AssetUpdate,
  layersKeyedById: Map<string, Layer>,
) => {
  const flattenedCurrentAsset = assetToHumanReadableRecord(currentAsset, layersKeyedById);
  const flattenedUpdatedAsset = assetUpdateToHumanReadableRecord(updatedAsset, layersKeyedById);
  // Only compare fields that exist in the updated asset
  // If the input csv is missing a column, that field is unmodified
  const filteredFlattenedCurrentAsset = pick(
    flattenedCurrentAsset,
    Object.keys(flattenedUpdatedAsset),
  );
  return getReadableRecordDiff(filteredFlattenedCurrentAsset, flattenedUpdatedAsset);
};

export const getAssetChangesKeyedByKey = (
  assetUpdates: AssetUpdate[] | undefined,
  key: string,
  assetsKeyedByKey: Map<string, Asset>,
  layersKeyedById: Map<string, Layer>,
) => {
  const updateMap = new Map<string, ReturnType<typeof getAssetChanges>>();
  assetUpdates?.forEach((updatedAsset) => {
    const k = updatedAsset[key];
    const currentAsset = assetsKeyedByKey.get(k);
    updateMap.set(k, getAssetChanges(
      currentAsset,
      updatedAsset,
      layersKeyedById,
    ));
  });
  return updateMap;
};

export const plannedTwinIDToHumanReadableRecord = (
  plannedTwinID: PlannedTwinID | undefined,
  statusesKeyedById: Map<string, AssetStatus>,
): Record<string, string> => {
  if (!plannedTwinID) return {};
  const { twinID, sequence, plannedStatusDates } = plannedTwinID;
  const statusDates: Record<string, string> = Object.keys(plannedStatusDates ?? {})
    .reduce((acc, statusID) => {
      const status = statusesKeyedById.get(statusID);
      return {
        ...acc,
        [status?.label ?? statusID]: displayDateInProjectTime(plannedStatusDates[statusID]),
      };
    }, {});

  return {
    twinID,
    sequence: `${sequence}`,
    ...statusDates,
  };
};

export const getPlannedTwinIDChanges = (
  currentPlannedTwinID: PlannedTwinID | undefined,
  updatedPlannedTwinID: PlannedTwinID,
  statusesKeyedById: Map<string, AssetStatus>,
) => {
  const flattenedCurrentPlannedTwinID = plannedTwinIDToHumanReadableRecord(
    currentPlannedTwinID,
    statusesKeyedById,
  );
  const flattenedUpdatedPlannedTwinID = plannedTwinIDToHumanReadableRecord(
    updatedPlannedTwinID,
    statusesKeyedById,
  );
  return getReadableRecordDiff(flattenedCurrentPlannedTwinID, flattenedUpdatedPlannedTwinID);
};
